import { useState, useEffect, useRef } from 'react'
import api from '../lib/api'
import './DeliberationTheatre.css'
import CouncilBuilder from './CouncilBuilder'
import TitansBattle from './TitansBattle'

// Model definitions - THE CHARACTERS
const MODELS = {
  'HAIKU': { role: 'Sentiment', color: '#2aa198', icon: '🐦' },
  'GROK': { role: 'Contrarian', color: '#d33682', icon: '⚡' },
  'SONNET': { role: 'Deep Research', color: '#268bd2', icon: '🎵' },
  'GEMINI': { role: 'Technical', color: '#6c71c4', icon: '♊' },
  'LLAMA': { role: 'Macro', color: '#cb4b16', icon: '🦙' },
  'OPUS': { role: 'The Judge', color: '#d4af37', icon: '⚖️' },
}

// Demo analysts for testing - 5 analysts, the full panel
const DEMO_ANALYSTS = [
  { model: 'HAIKU', role: 'Sentiment', position: 'FOR', confidence: 0.82, reasoning: 'Social sentiment overwhelmingly bullish. Retail FOMO building.' },
  { model: 'GROK', role: 'Contrarian', position: 'AGAINST', confidence: 0.68, reasoning: 'Echo chamber forming. When everyone agrees, be suspicious.' },
  { model: 'SONNET', role: 'Deep Research', position: 'FOR', confidence: 0.91, reasoning: 'Patent filings and hiring patterns suggest major product launch imminent.' },
  { model: 'GEMINI', role: 'Technical', position: 'FOR', confidence: 0.75, reasoning: 'Breaking out of 3-month consolidation. Volume confirming.' },
  { model: 'LLAMA', role: 'Macro', position: 'NEUTRAL', confidence: 0.55, reasoning: 'Fed uncertainty could override any sector-specific catalysts.' },
]

export default function DeliberationTheatre({ user, watchlist, onAddToWatchlist }) {
  const [query, setQuery] = useState('')
  const [mode, setMode] = useState('theatre') // 'theatre' or 'fast'
  const [eliteTab, setEliteTab] = useState('standard') // 'standard', 'council', 'titans'
  const [phase, setPhase] = useState('idle') // idle, query, signals, analysts, judge, verdict
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedCouncil, setSelectedCouncil] = useState(null)

  // Deliberation state
  const [signalsCount, setSignalsCount] = useState(0)
  const [analysts, setAnalysts] = useState([])
  const [visibleAnalysts, setVisibleAnalysts] = useState([])
  const [votes, setVotes] = useState({ for: 0, against: 0, neutral: 0 })
  const [verdict, setVerdict] = useState(null)
  const [showSkip, setShowSkip] = useState(false)

  const timerRef = useRef(null)

  // Timing based on mode
  // Theatre = ~15 second show (snappy but dramatic), Fast = ~5 seconds
  const timing = mode === 'theatre' ? {
    queryReveal: 1000,      // 0-1s: Question appears
    anticipationPause: 1000, // 1-2s: Build anticipation
    analystThinking: 1500,  // 1.5s thinking per analyst
    analystReveal: 500,     // 0.5s pause after reveal
    analystStagger: 2000,   // Total 2s per analyst (5 analysts = 10s)
    voteTally: 1000,        // Vote bar animates
    judgeThinking: 1500,    // OPUS synthesizing
    verdictReveal: 1000,    // Verdict slides up
  } : {
    queryReveal: 300,
    anticipationPause: 300,
    analystThinking: 400,
    analystReveal: 150,
    analystStagger: 550,
    voteTally: 300,
    judgeThinking: 400,
    verdictReveal: 300,
  }

  // Reset state
  const reset = () => {
    setPhase('idle')
    setSignalsCount(0)
    setAnalysts([])
    setVisibleAnalysts([])
    setVotes({ for: 0, against: 0, neutral: 0 })
    setVerdict(null)
    setShowSkip(false)
    setError('')
    if (timerRef.current) clearTimeout(timerRef.current)
  }

  // Skip animation
  const skip = () => {
    setVisibleAnalysts(analysts)
    setPhase('verdict')
    if (verdict) {
      // Already have verdict
    }
  }

  // Calculate votes from analysts
  useEffect(() => {
    const newVotes = { for: 0, against: 0, neutral: 0 }
    visibleAnalysts.forEach(a => {
      if (a.position === 'FOR') newVotes.for++
      else if (a.position === 'AGAINST') newVotes.against++
      else newVotes.neutral++
    })
    setVotes(newVotes)
  }, [visibleAnalysts])

  // Phase transitions for theatre animation
  useEffect(() => {
    if (phase === 'query') {
      // Query visible, then pause for anticipation
      timerRef.current = setTimeout(() => setPhase('signals'), timing.queryReveal + timing.anticipationPause)
    } else if (phase === 'signals') {
      // Signals badge shown briefly, then analysts start
      timerRef.current = setTimeout(() => setPhase('analysts'), 1000)
    } else if (phase === 'analysts') {
      // Check if all analysts have revealed their positions
      const allRevealed = visibleAnalysts.length === analysts.length &&
                          visibleAnalysts.every(a => a.revealed)
      if (allRevealed && analysts.length > 0) {
        timerRef.current = setTimeout(() => setPhase('judge'), timing.voteTally)
      }
    } else if (phase === 'judge') {
      timerRef.current = setTimeout(() => setPhase('verdict'), timing.judgeThinking)
    }

    // Show skip button after 5 seconds
    if (phase !== 'idle' && phase !== 'verdict') {
      const skipTimer = setTimeout(() => setShowSkip(true), 5000)
      return () => {
        clearTimeout(skipTimer)
        if (timerRef.current) clearTimeout(timerRef.current)
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [phase, visibleAnalysts, analysts.length])

  // Stagger analyst reveals - each analyst gets full timing
  useEffect(() => {
    if (phase === 'analysts' && analysts.length > 0) {
      analysts.forEach((analyst, i) => {
        // Analyst card appears at staggered intervals
        const appearTime = i * timing.analystStagger

        setTimeout(() => {
          setVisibleAnalysts(prev => {
            if (prev.find(a => a.model === analyst.model)) return prev
            return [...prev, { ...analyst, revealed: false }]
          })
        }, appearTime)

        // Position reveals after thinking time
        setTimeout(() => {
          setVisibleAnalysts(prev =>
            prev.map(a => a.model === analyst.model ? { ...a, revealed: true } : a)
          )
        }, appearTime + timing.analystThinking)
      })
    }
  }, [phase, analysts])

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!query.trim() || loading) return

    // Check tier limits
    if (user?.tier === 'free' && user?.daily_deliberations >= user?.daily_limit) {
      setError('Daily limit reached. Upgrade to Pro for unlimited deliberations.')
      return
    }

    reset()
    setLoading(true)
    setPhase('query')

    try {
      // Call real API - use council mode if analysts are selected
      const result = selectedCouncil && isElite
        ? await api.deliberateCouncil(query, selectedCouncil, selectedJudge)
        : await api.deliberate(query)

      console.log('API response:', result)

      // Check for API error
      if (!result.success || result.error) {
        throw new Error(result.error || 'Deliberation failed')
      }

      const v = result.verdict

      // Use real analyst positions from API
      const apiAnalysts = (result.analyst_positions || []).map(a => ({
        model: a.model?.toUpperCase() || 'ANALYST',
        role: a.role || MODELS[a.model?.toUpperCase()]?.role || 'Analyst',
        position: a.position?.toUpperCase() || 'NEUTRAL',
        confidence: a.confidence || 0.5,
        reasoning: a.reasoning || 'Analysis in progress...',
        risks: a.risks || []
      }))

      // Fallback to demo if no analysts returned
      const finalAnalysts = apiAnalysts.length > 0 ? apiAnalysts : DEMO_ANALYSTS
      setAnalysts(finalAnalysts)

      // Set signals count (estimate based on latency)
      const estimatedSignals = Math.floor((result.latency_ms || 15000) / 300)
      setSignalsCount(estimatedSignals)

      // Transform verdict from new API response format
      const apiVerdict = {
        ruling: v.ruling || 'NEUTRAL',
        confidence: v.confidence || 0.5,
        headline: v.headline || 'Analysis complete',
        thesis: v.thesis || v.headline || 'See key evidence below',
        entry: null, // Not provided by current API
        stop: null,
        target: null,
        key_evidence: v.key_evidence || [],
        dissenting_views: v.dissenting_views || []
      }

      // Set verdict after animation plays
      const animationDuration = timing.queryReveal + 1000 + (finalAnalysts.length * timing.analystStagger) + timing.judgeThinking
      setTimeout(() => setVerdict(apiVerdict), animationDuration)

    } catch (err) {
      // If API fails, fall back to demo mode
      console.error('API call failed, using demo mode:', err.message, err)

      // Simulate signal collection
      setTimeout(() => setSignalsCount(47 + Math.floor(Math.random() * 100)), timing.queryReveal * 0.5)

      // Use demo analysts
      setAnalysts(DEMO_ANALYSTS)

      // Create demo verdict
      const demoVerdict = {
        ruling: 'FOR',
        confidence: 0.84,
        headline: 'Evidence supports bullish outlook',
        thesis: 'Multiple converging signals indicate positive momentum. Technical breakout confirmed by volume. Sentiment shift detected in institutional flows.',
        entry: '$142.50',
        stop: '$138.00',
        target: '$165.00',
        key_evidence: [
          'Unusual options activity in $155 calls',
          'Social sentiment at 6-month high',
          'Breaking 200-day moving average with volume'
        ]
      }

      setTimeout(() => setVerdict(demoVerdict), timing.queryReveal + 1000 + (DEMO_ANALYSTS.length * timing.analystStagger) + timing.judgeThinking)
    } finally {
      setLoading(false)
    }
  }

  const getPositionColor = (position) => {
    if (position === 'FOR') return 'var(--color-for)'
    if (position === 'AGAINST') return 'var(--color-against)'
    return 'var(--color-neutral)'
  }

  const totalVotes = votes.for + votes.against + votes.neutral
  const forPct = totalVotes > 0 ? (votes.for / totalVotes) * 100 : 0
  const againstPct = totalVotes > 0 ? (votes.against / totalVotes) * 100 : 0

  // Handle council deliberation with custom analysts
  const [selectedJudge, setSelectedJudge] = useState('opus')

  const handleCouncilReady = (councilIds, judge) => {
    setSelectedCouncil(councilIds)
    setSelectedJudge(judge)
    // Keep council tab active - don't reset to standard
    // Council selection persists until deliberation completes
  }

  // Handle titans battle completion
  const handleTitansComplete = (result) => {
    console.log('Titans battle result:', result)
  }

  const isElite = user?.tier === 'elite'

  return (
    <div className="deliberation-theatre">
      {/* Elite Tabs - Show for all paid users, locked for non-Elite */}
      {user?.tier !== 'free' && phase === 'idle' && (
        <div className="elite-tabs">
          <button
            className={`elite-tab ${eliteTab === 'standard' ? 'active' : ''}`}
            onClick={() => setEliteTab('standard')}
          >
            Standard
          </button>
          <button
            className={`elite-tab ${eliteTab === 'council' ? 'active' : ''} ${!isElite ? 'locked' : ''}`}
            onClick={() => isElite && setEliteTab('council')}
            disabled={!isElite}
            title={!isElite ? 'Elite tier required' : ''}
          >
            {!isElite && '🔒 '}Build Council
          </button>
          <button
            className={`elite-tab titans ${eliteTab === 'titans' ? 'active' : ''} ${!isElite ? 'locked' : ''}`}
            onClick={() => isElite && setEliteTab('titans')}
            disabled={!isElite}
            title={!isElite ? 'Elite tier required' : ''}
          >
            {!isElite && '🔒 '}⚔️ Titans
          </button>
        </div>
      )}

      {/* Council Builder - Elite Only */}
      {isElite && eliteTab === 'council' && phase === 'idle' && (
        <CouncilBuilder
          onCouncilReady={handleCouncilReady}
          disabled={loading}
        />
      )}

      {/* Titans Battle - Elite Only */}
      {isElite && eliteTab === 'titans' && (
        <TitansBattle
          onComplete={handleTitansComplete}
          onVote={(titan) => console.log('User voted for:', titan)}
        />
      )}

      {/* Standard Mode Controls */}
      {(eliteTab === 'standard' || !isElite) && (
        <>
          <div className="theatre-controls">
            <div className="mode-toggle">
              <button
                className={`mode-btn ${mode === 'theatre' ? 'active' : ''}`}
                onClick={() => setMode('theatre')}
              >
                Theatre
              </button>
              <button
                className={`mode-btn ${mode === 'fast' ? 'active' : ''}`}
                onClick={() => setMode('fast')}
              >
                Fast
              </button>
            </div>
            {selectedCouncil && isElite && phase === 'idle' && (
              <div className="council-badge">
                Council: {selectedCouncil.length} analysts
                <button className="clear-council" onClick={() => setSelectedCouncil(null)}>×</button>
              </div>
            )}
            {phase !== 'idle' && phase !== 'verdict' && showSkip && (
              <button className="skip-btn" onClick={skip}>Skip →</button>
            )}
            {phase === 'verdict' && (
              <button className="reset-btn" onClick={() => { reset(); setQuery('') }}>New Query</button>
            )}
          </div>

      {/* Query Input */}
      {phase === 'idle' && (
        <>
          <form onSubmit={handleSubmit} className="query-form">
            <input
              type="text"
              className="query-input"
              placeholder="Ask The Judge..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={loading || user?.tier === 'free'}
              autoFocus
            />
            <button
              type="submit"
              className="query-submit"
              disabled={loading || !query.trim() || user?.tier === 'free'}
            >
              {loading ? 'Deliberating...' : 'Deliberate'}
            </button>
          </form>

          {/* Upgrade CTA for free tier */}
          {user?.tier === 'free' && (
            <div className="upgrade-cta-box">
              <p style={{ color: 'var(--text-bright)', marginBottom: 'var(--space-2)', fontSize: 'var(--text-base)', fontWeight: 500 }}>
                Ready to ask The Judge?
              </p>
              <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
                Watch the signal feed and track record while you explore. Unlock deliberations for $10/mo.
              </p>
              <a
                href="/pricing"
                className="upgrade-btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'linear-gradient(135deg, var(--accent-green), var(--accent-cyan))',
                  color: 'white',
                  borderRadius: 'var(--radius-md)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 'var(--text-sm)'
                }}
              >
                Unlock Full Access — $10/mo
              </a>
            </div>
          )}
        </>
      )}

      {error && (
        <div className="theatre-error">{error}</div>
      )}

      {/* STAGE */}
      <div className={`stage ${phase}`}>

        {/* Query Display */}
        {phase !== 'idle' && (
          <div className={`stage-query ${phase !== 'idle' ? 'visible' : ''}`}>
            <div className="stage-query-label">THE QUESTION</div>
            <h2 className="stage-query-text">{query}</h2>
          </div>
        )}

        {/* Signals Badge */}
        {(phase === 'signals' || phase === 'analysts' || phase === 'judge' || phase === 'verdict') && (
          <div className="signals-badge">
            <span className="signals-count">{signalsCount}</span>
            <span className="signals-label">signals analyzed</span>
          </div>
        )}

        {/* Analyst Cards - MODEL NAME FIRST */}
        {(phase === 'analysts' || phase === 'judge' || phase === 'verdict') && (
          <div className="analysts-stage">
            {visibleAnalysts.map((analyst, i) => (
              <div
                key={analyst.model}
                className={`analyst-card ${analyst.revealed ? 'revealed' : ''} ${analyst.position?.toLowerCase() || ''}`}
                style={{ '--index': i, '--color': MODELS[analyst.model]?.color || 'var(--accent-blue)' }}
              >
                <div className="analyst-model-name">{analyst.model}</div>
                <div className="analyst-role">{analyst.role}</div>

                {!analyst.revealed && (
                  <div className="analyst-thinking">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                )}

                {analyst.revealed && (
                  <>
                    <div className="analyst-reasoning">"{analyst.reasoning}"</div>
                    {analyst.risks && analyst.risks.length > 0 && (
                      <div className="analyst-risks">
                        {analyst.risks.slice(0, 2).map((risk, j) => (
                          <span key={j} className="risk-tag">⚠️ {risk}</span>
                        ))}
                      </div>
                    )}
                    <div className="analyst-verdict">
                      <span className={`position-badge ${analyst.position?.toLowerCase()}`}>
                        {analyst.position}
                      </span>
                      <span className="confidence-badge">
                        {Math.round(analyst.confidence * 100)}%
                      </span>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Vote Tally */}
        {totalVotes > 0 && (phase === 'analysts' || phase === 'judge' || phase === 'verdict') && (
          <div className="vote-tally">
            <div className="vote-labels">
              <span className="vote-label for">{votes.for} FOR</span>
              {votes.neutral > 0 && <span className="vote-label neutral">{votes.neutral} NEUTRAL</span>}
              <span className="vote-label against">{votes.against} AGAINST</span>
            </div>
            <div className="vote-bar">
              <div className="vote-for" style={{ width: `${forPct}%` }}></div>
              {votes.neutral > 0 && <div className="vote-neutral" style={{ width: `${(votes.neutral / totalVotes) * 100}%` }}></div>}
              <div className="vote-against" style={{ width: `${againstPct}%` }}></div>
            </div>
          </div>
        )}

        {/* The Judge */}
        {phase === 'judge' && (
          <div className="judge-section">
            <div className="judge-avatar">⚖️</div>
            <div className="judge-label">OPUS</div>
            <div className="judge-status">The Judge is synthesizing...</div>
            <div className="judge-thinking">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        )}

        {/* Verdict Card */}
        {phase === 'verdict' && verdict && (
          <div className={`verdict-card ${verdict.ruling?.toLowerCase()}`}>
            <div className="verdict-header">
              <span className={`verdict-ruling ${verdict.ruling?.toLowerCase()}`}>
                {verdict.ruling}
              </span>
              <span className="verdict-confidence">
                {Math.round(verdict.confidence * 100)}%
              </span>
            </div>

            <h3 className="verdict-headline">{verdict.headline}</h3>
            <p className="verdict-thesis">{verdict.thesis}</p>

            {/* Action Box - Pro/Elite only */}
            {user?.tier !== 'free' && verdict.entry && (
              <div className="verdict-action">
                <div className="action-row">
                  <div className="action-item">
                    <span className="action-label">Entry</span>
                    <span className="action-value">{verdict.entry}</span>
                  </div>
                  <div className="action-item stop">
                    <span className="action-label">Stop</span>
                    <span className="action-value">{verdict.stop}</span>
                  </div>
                  <div className="action-item target">
                    <span className="action-label">Target</span>
                    <span className="action-value">{verdict.target}</span>
                  </div>
                </div>
              </div>
            )}

            {verdict.key_evidence && verdict.key_evidence.length > 0 && (
              <div className="verdict-evidence">
                <div className="evidence-title">Key Evidence</div>
                <ul>
                  {verdict.key_evidence.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}

            {verdict.dissenting_views && verdict.dissenting_views.length > 0 && (
              <div className="verdict-evidence dissenting">
                <div className="evidence-title">Dissenting Views</div>
                <ul>
                  {verdict.dissenting_views.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}

            <button
              className="add-watchlist-btn"
              onClick={() => {
                // Extract ticker from query (simplified)
                const ticker = query.match(/\$?([A-Z]{1,5})/)?.[1]
                if (ticker) onAddToWatchlist(ticker)
              }}
            >
              + Add to Watchlist
            </button>

            {user?.tier === 'free' && (
              <div className="upgrade-prompt">
                <p>Upgrade to Pro for entry/stop/target recommendations</p>
                <a href="/pricing" className="upgrade-btn">Upgrade Now</a>
              </div>
            )}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  )
}
